import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { blue, green, orange, purple, red } from "@mui/material/colors";
import {
  Attachment as AttachmentIcon,
  Block as BlockIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  FileOpen as FileOpenIcon,
  PictureAsPdf as PictureAsPdfIcon,
  RestorePage as RestorePageIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import RegularButton from "../../components/CustomButtons/Button";
import { ModalV2 } from "../../components/Modal/ModalV2";
import { useToggle } from "../../hooks/useToggle";
import { useRuleValidator } from "../../hooks/useRuleValidator";
import { PERMISSIONS } from "../../router/permissions";
import {
  setFormCancelInvoice,
  downloadInvoiceFile,
  selectInvoiceId,
  emailModal,
  setFormSubstituteInvoice,
  downloadPromissoryNote,
  regenerateFilesInvoice,
  checkNoteMigrations,
} from "../../redux/actions/invoices/invoicesAction";
import { lastMonth } from "./dateValues";
import { canCancelInvoice } from "./cancelValidation";
import { ModalRegenerateFiles } from "./ModalRegenerateFIles";

export const columns = [
  {
    Header: "Empresa",
    accessor: "companyName",
    filterable: true,
    filterMethod: (filter, row) =>
      row.companyName
        ? row.companyName.toLowerCase().includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "Planta",
    accessor: "station",
    filterable: true,
    filterMethod: (filter, row) =>
      row.station
        ? row.station.toLowerCase().includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "RFC",
    accessor: "rfc",
    filterable: true,
    filterMethod: (filter, row) =>
      row.rfc ? row.rfc.toLowerCase().includes(filter.value.toLowerCase()) : "",
  },
  {
    Header: "Folio",
    accessor: "folio",
    filterable: true,
    filterMethod: (filter, row) =>
      row.folio
        ? row.folio.toLowerCase().includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "Serie",
    accessor: "serie",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.serie)
        ? String(row.serie).toLowerCase().includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "Fecha",
    accessor: "date",
    filterable: true,
    filterMethod: (filter, row) =>
      row.date
        ? row.date.toLowerCase().includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "Estatus",
    accessor: "status",
    filterable: true,
    filterMethod: (filter, row) =>
      row.status
        ? row.status.toLowerCase().includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "Pago",
    accessor: "payment",
    filterable: true,
    filterMethod: (filter, row) =>
      row.payment
        ? row.payment.toLowerCase().includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "Cliente",
    accessor: "customer",
    filterable: true,
    filterMethod: (filter, row) =>
      row.customer
        ? row.customer.toLowerCase().includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "UUID",
    accessor: "fiscalID",
    filterable: true,
    filterMethod: (filter, row) =>
      row.fiscalID
        ? row.fiscalID.toLowerCase().includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "Cantidad",
    accessor: "quantity",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.quantity)
        ? String(row.quantity)
            .toLowerCase()
            .includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "Subtotal",
    accessor: "subTotal",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.subTotal)
        ? String(row.subTotal)
            .toLowerCase()
            .includes(filter.value.toLowerCase())
        : "",
  },
  {
    Header: "Total",
    accessor: "total",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.total) ? String(row.total).includes(filter.value) : "",
  },
  {
    Header: "Acciones",
    Cell: ({ original }) => {
      const dispatch = useDispatch();
      const { isLoadingComp } = useSelector(({ search }) => search);
      const isPromissory = useRuleValidator(PERMISSIONS.INVOICES_PROMISSORY_NOTE);
      const canDelete = useRuleValidator(PERMISSIONS.INVOICES_GLOBAL_DELETE);
      const {
        status,
        date,
        id,
        accuseToken,
        isGlobal,
        hasPdf,
        hasXml,
      } = original;
      const [openModal, setOpenModal] = useToggle();
      const invoiceDate = new Date(date);
      const [logo, setLogo] = useState();
      const [comment, setComment] = useState("");
      const [hasBankReferences, setHasBankReferences] = useState(true);

      const dateValidationCancelation = canCancelInvoice(date, isGlobal);

      const dateValidationSustitution = invoiceDate.getMonth() >= lastMonth;

      const openSustituteForm = () => {
        dispatch(setFormSubstituteInvoice(true));
        dispatch(selectInvoiceId(id));
      };

      const openCancelForm = () => {
        dispatch(setFormCancelInvoice(true));
        dispatch(selectInvoiceId(id));
        dispatch(checkNoteMigrations(id));
      };

      const handleOpenSendEmail = () => {
        dispatch(emailModal(true));
        dispatch(selectInvoiceId(id));
      };

      const handleDownloadXml = (active = true, accuse = false) => {
        dispatch(downloadInvoiceFile(id, active, accuse));
      };

      const handleDownloadPromissory = () => {
        dispatch(downloadPromissoryNote(original?.id));
      };

      const handleRegenerateInvoice = (isPdf) => {
        const form = isPdf
          ? {
              logo: logo?.logo,
              addBankReferences: hasBankReferences,
              observationsOrUnificationNotesData: comment,
            }
          : {};
        dispatch(
          regenerateFilesInvoice(
            original?.id,
            form,
            original?.folio,
            ...(isPdf ? [setOpenModal] : []),
          ),
        );
      };

      return (
        <>
          <Box
            style={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {hasXml ? (
              <RegularButton
                color="transparent"
                size="sm"
                justIcon
                round
                tooltip="Descargar XML"
                disabled={isLoadingComp?.downloadInvoiceLoading}
                onClick={() => handleDownloadXml(false)}
              >
                <DescriptionIcon style={{ color: green[500] }} />
              </RegularButton>
            ) : (
              <RegularButton
                color="transparent"
                size="sm"
                justIcon
                round
                tooltip="Regenerar XML"
                disabled={isLoadingComp?.downloadInvoiceLoading}
                onClick={() =>
                  hasPdf ? handleRegenerateInvoice(false) : setOpenModal()
                }
              >
                <FileOpenIcon style={{ color: red[500] }} />
              </RegularButton>
            )}
            {hasPdf ? (
              <RegularButton
                color="transparent"
                size="sm"
                justIcon
                round
                tooltip="Descargar PDF"
                disabled={isLoadingComp?.downloadInvoiceLoading}
                onClick={() => handleDownloadXml(true)}
              >
                <PictureAsPdfIcon style={{ color: blue[500] }} />
              </RegularButton>
            ) : (
              <RegularButton
                color="transparent"
                size="sm"
                justIcon
                round
                tooltip="Regenerar PDF"
                disabled={isLoadingComp?.downloadInvoiceLoading}
                onClick={setOpenModal}
              >
                <UploadFileIcon style={{ color: red[500] }} />
              </RegularButton>
            )}
            {status === "VIGENTE" ? (
              <>
                <RegularButton
                  color="transparent"
                  size="sm"
                  justIcon
                  round
                  tooltip="Reenviar"
                  disabled={isLoadingComp?.forwardEmailLoading}
                  onClick={handleOpenSendEmail}
                >
                  <EmailIcon style={{ color: blue[300] }} />
                </RegularButton>

                {canDelete &&
                dateValidationCancelation ? (
                  <RegularButton
                    color="transparent"
                    size="sm"
                    justIcon
                    round
                    tooltip="Cancelar Factura"
                    onClick={openCancelForm}
                  >
                    <BlockIcon style={{ color: red[500] }} />
                  </RegularButton>
                ) : (
                  !original.isGlobal &&
                  dateValidationSustitution && (
                    <RegularButton
                      color="transparent"
                      size="sm"
                      justIcon
                      round
                      tooltip="Sustituir Factura"
                      onClick={openSustituteForm}
                    >
                      <RestorePageIcon style={{ color: orange[500] }} />
                    </RegularButton>
                  )
                )}
              </>
            ) : null}
            {accuseToken && (
              <RegularButton
                color="transparent"
                size="sm"
                justIcon
                round
                tooltip="Acuse Cancelación"
                onClick={() => handleDownloadXml(false, true)}
                loading={isLoadingComp?.downloadInvoiceLoading}
              >
                <AttachmentIcon style={{ color: purple[200] }} />
              </RegularButton>
            )}
            {isPromissory && (
              <RegularButton
                color="transparent"
                size="sm"
                justIcon
                round
                tooltip="Generar Pagaré"
                onClick={handleDownloadPromissory}
              >
                <DescriptionIcon style={{ color: red[500] }} />
              </RegularButton>
            )}
          </Box>
          <ModalV2
            open={openModal}
            onClose={setOpenModal}
            title={`Regenerar PDF ${
              original?.isXml ? "" : "y XML"
            } de la factura ${original?.folio}`}
            onSubmit={() => handleRegenerateInvoice(true)}
            isLoading={isLoadingComp?.downloadInvoiceLoading}
            toRight
          >
            <ModalRegenerateFiles
              idStation={original?.idStation}
              logo={logo}
              comment={comment}
              hasBankReferences={hasBankReferences}
              setComment={setComment}
              setLogo={setLogo}
              setHasBankReferences={setHasBankReferences}
            />
          </ModalV2>
        </>
      );
    },
  },
];
